// PreviousResultsPage — Replit-parity Previous Results Page with collapsible filters
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RESEARCH_MODE_LABEL, researchModeOf } from '@/features/research/researchMode';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Select, Tabs } from '@/components/ui';
import QueryError from '@/components/feedback/QueryError';
import { resultsService, peTearsheetService, researchService } from '@/services/api';
import type { FiltersApplied, ResultSummary, ResultTab, TearsheetSummary } from '@/types';
import type { ResultsPagination, ResultUser } from '@/services/api/resultsService';
import {
  filtersToFvForm,
  filtersToStrategicForm,
  filtersToTargetForm,
} from '@/features/research/filtersToForm';
import { stashDraft as stashStrategicDraft } from '@/features/research/strategic/schema';
import { stashDraft as stashTargetDraft } from '@/features/research/target/schema';
import { stashFinancialDraft } from '@/features/research/financial/draft';
import { useToast } from '@/hooks/useToast';
import { paths } from '@/routes/paths';
import { resultCrumbName } from '@/components/layout/breadcrumbs';
import { buildTearsheetPath } from '@/lib/tearsheet/openTearsheet';
import { filterAndSortTearsheets } from '@/lib/tearsheet/filterTearsheets';
import { useAuthStore } from '@/store/authStore';
import PreviousResultExpandPanel from '@/features/results/PreviousResultExpandPanel';
import '@/styles/pages/previous-results-replit.css';

const TEARSHEETS_PER_PAGE = 10;

const TIME_RANGE_OPTIONS = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'week', label: 'Last 7 Days' },
  { value: 'month', label: 'Last 30 Days' },
  { value: '3months', label: 'Last 3 Months' },
  { value: '6months', label: 'Last 6 Months' },
  { value: 'year', label: 'Last Year' },
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
];

type CheckGroup = 'common' | 'tl-sb' | 'fv';
interface CheckDef {
  id: string;
  value: string;
  label: string;
  group: CheckGroup;
}

const APPLIED_FILTER_CHECKS: CheckDef[] = [
  {
    id: 'filter_business_query',
    value: 'has_business_query',
    label: 'Business Query',
    group: 'common',
  },
  {
    id: 'filter_custom_insights',
    value: 'has_custom_insights',
    label: 'Custom Insights',
    group: 'tl-sb',
  },
  {
    id: 'filter_current_portfolio',
    value: 'has_current_portfolio',
    label: 'Current Portfolio',
    group: 'fv',
  },
  {
    id: 'filter_past_portfolio',
    value: 'has_past_portfolio',
    label: 'Past Portfolio',
    group: 'fv',
  },
  {
    id: 'filter_listed_interest',
    value: 'has_listed_interest',
    label: 'Listed Interest',
    group: 'fv',
  },
  { id: 'filter_industry', value: 'has_industry', label: 'Industry', group: 'common' },
  { id: 'filter_geography', value: 'has_geography', label: 'Geography', group: 'common' },
  { id: 'filter_revenue', value: 'has_revenue', label: 'Revenue', group: 'tl-sb' },
  { id: 'filter_employees', value: 'has_employees', label: 'Employees', group: 'tl-sb' },
  {
    id: 'filter_primary_activity',
    value: 'has_primary_activity',
    label: 'Primary Activity',
    group: 'tl-sb',
  },
  {
    id: 'filter_secondary_activity',
    value: 'has_secondary_activity',
    label: 'Secondary Activity',
    group: 'tl-sb',
  },
  {
    id: 'filter_primary_business_only',
    value: 'has_primary_business_only',
    label: 'Primary Business Only',
    group: 'tl-sb',
  },
  {
    id: 'filter_apollo_enrichment',
    value: 'has_apollo_enrichment',
    label: 'Apollo Enrichment',
    group: 'tl-sb',
  },
  {
    id: 'filter_news_enrichment',
    value: 'has_news_enrichment',
    label: 'News Enrichment',
    group: 'tl-sb',
  },
];

const DATA_SOURCE_CHECKS: CheckDef[] = [
  {
    id: 'filter_apollo_search',
    value: 'has_apollo_search',
    label: 'Apollo Search',
    group: 'tl-sb',
  },
  { id: 'filter_gmaps_search', value: 'has_gmaps_search', label: 'GMaps Search', group: 'tl-sb' },
  {
    id: 'filter_coresignal_search',
    value: 'has_coresignal_search',
    label: 'Coresignal Search',
    group: 'tl-sb',
  },
];

const TABS: {
  tabType: ResultTab;
  id: string;
  label: string;
  buildPath?: string;
  emptyMode?: string;
}[] = [
  {
    tabType: 'target-list',
    id: 'target-list-tab',
    label: 'Target lists',
    buildPath: paths.targetList,
    emptyMode: 'target list',
  },
  {
    tabType: 'strategic-buyer',
    id: 'strategic-buyer-tab',
    label: 'Strategic',
    buildPath: paths.strategic,
    emptyMode: 'strategic buyer list',
  },
  {
    tabType: 'fv-results',
    id: 'fv-results-tab',
    label: 'Financials',
    buildPath: paths.financialVerticals,
    emptyMode: 'financials buyer list',
  },
  { tabType: 'tearsheets', id: 'tearsheets-tab', label: 'Tearsheets' },
];

const VALID_TABS: ResultTab[] = ['target-list', 'strategic-buyer', 'fv-results', 'tearsheets'];

type NumberLike = number | string;

interface ReusableFilters extends FiltersApplied {
  business_queries?: string[];
  industry_pairs?: { industry: string; subIndustry?: string }[];
  location_groups?: { continent?: string; country?: string; state?: string; city?: string }[];
  custom_insights?: { questions?: string[] };
  llm_providers?: string[] | null;
  primary_activity?: string;
  secondary_activity?: string;
  min_revenue?: NumberLike;
  max_revenue?: NumberLike;
  min_employees?: NumberLike;
  max_employees?: NumberLike;
  size_criteria_logic?: string;
  use_news?: boolean;
  use_apollo?: boolean;
  enable_linkedin_enrichment?: boolean;
  ownership_enrichment?: boolean;
  acquisition_enrichment?: boolean;
  blank_field_backfill?: boolean;
  blankFieldBackfill?: boolean;
  llm_fallback_enabled?: boolean;
  targetDescription?: string;
  target_description?: string;
  businessType?: string;
  business_type?: string;
  sub_industry?: string;
  continent?: string;
  country?: string;
  state?: string;
  size?: {
    revenueMin?: NumberLike;
    revenueMax?: NumberLike;
    ebitdaMin?: NumberLike;
    ebitdaMax?: NumberLike;
  };
  exposure?: {
    currentPortfolio?: boolean;
    pastPortfolio?: boolean;
    listedInterest?: boolean;
  };
  ebitdaMin?: NumberLike;
  ebitdaMax?: NumberLike;
  equityCheckMin?: NumberLike;
  equityCheckMax?: NumberLike;
  enterpriseValueMin?: NumberLike;
  enterpriseValueMax?: NumberLike;
  // F67 — the backend persists both: `platform_floors` is what was applied (defaults
  // filled in), `platform_floors_raw` is what was typed. Reruns read the raw pair so
  // blanks stay blank. Both casings are accepted; the envelope may camelCase them.
  platformFloorsRaw?: { ebitda?: NumberLike; revenue?: NumberLike };
  platform_floors_raw?: { ebitda?: NumberLike; revenue?: NumberLike };
  platformFloors?: { ebitda?: NumberLike; revenue?: NumberLike };
  currentPortfolio?: boolean;
  pastPortfolio?: boolean;
  listedInterest?: boolean;
  autoEnrich?: boolean;
  requestPeContact?: boolean;
  requestContact?: boolean;
}

type LegacyResultSummary = ResultSummary & { filters_applied?: ReusableFilters };

function parseTab(raw: string | null): ResultTab {
  return raw && (VALID_TABS as string[]).includes(raw) ? (raw as ResultTab) : 'target-list';
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function statusVariant(status: string | null | undefined): string {
  const s = (status || '').toLowerCase();
  if (s === 'completed' || s === 'complete') return 'bg-status-success';
  if (s.includes('cancelled')) return 'bg-status-cancelled';
  if (s === 'error' || s === 'failed') return 'bg-status-error';
  if (
    s === 'processing' ||
    s === 'running' ||
    s === 'pending' ||
    s === 'researching' ||
    s === 'synthesizing' ||
    s === 'polishing' ||
    s === 'generating'
  )
    return 'bg-status-pending';
  return 'bg-status-default';
}

function statusLabel(status: string | null | undefined): string {
  const s = (status || '').toLowerCase();
  if (s === 'complete' || s === 'completed') return 'Completed';
  if (s === 'polishing') return 'Polishing';
  if (s === 'generating') return 'Polishing';
  if (s === 'researching') return 'Researching';
  if (s === 'synthesizing') return 'Synthesizing';
  if (s === 'pending') return 'Pending';
  if (s === 'failed' || s === 'error') return 'Failed';
  if (s.includes('cancelled_by_user')) return 'Cancelled by user';
  return status || 'Unknown';
}

export default function PreviousResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  const activeTab = parseTab(searchParams.get('active_tab'));
  const activeTabDef = TABS.find((t) => t.tabType === activeTab)!;

  // Server-side filter form state (seeded from URL for deep links / refresh).
  const [timeRange, setTimeRange] = useState(searchParams.get('time_range') || 'all');
  const [sortBy, setSortBy] = useState(searchParams.get('sort_by') || 'newest');
  const [userFilter, setUserFilter] = useState(searchParams.get('user_filter') || 'all');
  const [filenameSearch, setFilenameSearch] = useState(searchParams.get('filename_search') || '');
  const [selectedFilters, setSelectedFilters] = useState<string[]>(
    searchParams.getAll('filter_type'),
  );
  const [showFilters, setShowFilters] = useState(false);
  const [users, setUsers] = useState<ResultUser[]>([]);

  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [results, setResults] = useState<ResultSummary[]>([]);
  const [tearsheets, setTearsheets] = useState<TearsheetSummary[]>([]);
  const [pagination, setPagination] = useState<ResultsPagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const page = Number(searchParams.get('page') || '1') || 1;
  const reqIdRef = useRef(0);

  useEffect(() => {
    setExpandedId(null);
  }, [activeTab, page]);

  const userOptions = useMemo(
    () => [
      { value: 'all', label: 'All Users' },
      { value: 'me', label: 'My Results Only' },
      ...users.map((u) => ({ value: u.id, label: u.fullName })),
    ],
    [users],
  );

  const appliedFilterCount = useMemo(() => {
    const tsTab = activeTab === 'tearsheets';
    let n = tsTab ? 0 : selectedFilters.length;
    if (timeRange !== 'all') n += 1;
    if (sortBy !== 'newest') n += 1;
    if (userFilter !== 'all') n += 1;
    if (tsTab) {
      const company = (searchQuery || filenameSearch).trim();
      if (company) n += 1;
    } else if (filenameSearch.trim()) {
      n += 1;
    }
    return n;
  }, [activeTab, selectedFilters, timeRange, sortBy, userFilter, filenameSearch, searchQuery]);

  useEffect(() => {
    let cancelled = false;
    resultsService
      .listUsers()
      .then((res) => {
        if (!cancelled && res.success) setUsers(res.users);
      })
      .catch(() => {
        /* non-fatal — dropdown just won't list extra users */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setLoadError(false);

    if (activeTab === 'tearsheets') {
      peTearsheetService
        .list()
        .then((rows) => {
          if (reqId !== reqIdRef.current) return;
          setTearsheets(rows ?? []);
          setResults([]);
          setPagination(null);
        })
        .catch(() => {
          if (reqId !== reqIdRef.current) return;
          setTearsheets([]);
          setLoadError(true);
        })
        .finally(() => {
          if (reqId === reqIdRef.current) setLoading(false);
        });
      return;
    }

    const filenameParam = searchParams.get('filename_search') || undefined;
    resultsService
      .list(activeTab, {
        page,
        timeRange: searchParams.get('time_range') || 'all',
        sortBy: searchParams.get('sort_by') || 'newest',
        userFilter: searchParams.get('user_filter') || 'all',
        filenameSearch: activeTab !== 'fv-results' ? filenameParam : undefined,
        filterType: searchParams.getAll('filter_type'),
      })
      .then((data) => {
        if (reqId !== reqIdRef.current) return;
        if (data.success) {
          setResults(data.results);
          setPagination(data.pagination);
          setTearsheets([]);
        } else {
          setResults([]);
          setPagination(null);
        }
      })
      .catch(() => {
        if (reqId !== reqIdRef.current) return;
        setResults([]);
        setPagination(null);
        setLoadError(true);
      })
      .finally(() => {
        if (reqId === reqIdRef.current) setLoading(false);
      });
  }, [activeTab, page, searchParams]);

  useEffect(() => {
    if (activeTab !== 'tearsheets') return;
    const hasInFlight = tearsheets.some(
      (t) =>
        ['pending', 'researching', 'synthesizing', 'polishing'].includes(t.status) ||
        t.gammaStatus === 'generating',
    );
    if (!hasInFlight) return;
    const timer = setInterval(() => {
      peTearsheetService
        .list()
        .then((rows) => {
          setTearsheets(rows ?? []);
        })
        .catch(() => {
          /* transient poll error */
        });
    }, 3000);
    return () => clearInterval(timer);
  }, [activeTab, tearsheets]);

  const commitToUrl = useCallback(
    (tab: ResultTab, nextPage: number, overrides?: { reset?: boolean }) => {
      const next = new URLSearchParams();
      next.set('active_tab', tab);
      next.set('page', String(nextPage));
      if (overrides?.reset) {
        if (searchQuery.trim()) next.set('search', searchQuery.trim());
        setSearchParams(next);
        return;
      }
      if (timeRange && timeRange !== 'all') next.set('time_range', timeRange);
      if (sortBy && sortBy !== 'newest') next.set('sort_by', sortBy);
      if (userFilter && userFilter !== 'all') next.set('user_filter', userFilter);
      if (filenameSearch.trim() && tab !== 'fv-results') {
        next.set('filename_search', filenameSearch.trim());
      }
      selectedFilters.forEach((f) => next.append('filter_type', f));
      if (searchQuery.trim()) next.set('search', searchQuery.trim());
      setSearchParams(next);
    },
    [timeRange, sortBy, userFilter, filenameSearch, selectedFilters, searchQuery, setSearchParams],
  );

  // Sync search state when URL changes, mainly for deep links/back button
  useEffect(() => {
    setSearchQuery(searchParams.get('search') || '');
    setTimeRange(searchParams.get('time_range') || 'all');
    setSortBy(searchParams.get('sort_by') || 'newest');
    setUserFilter(searchParams.get('user_filter') || 'all');
    setFilenameSearch(searchParams.get('filename_search') || '');
    setSelectedFilters(searchParams.getAll('filter_type'));
  }, [searchParams]);

  // Handle enter key in search
  const handleSearchKeydown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      commitToUrl(activeTab, 1);
    }
  };

  const switchTab = (tab: ResultTab) => {
    if (tab === activeTab) return;
    setSearchQuery('');
    setSelectedFilters([]);
    setFilenameSearch('');
    setShowFilters(false);
    const next = new URLSearchParams();
    next.set('active_tab', tab);
    next.set('page', '1');
    if (timeRange && timeRange !== 'all') next.set('time_range', timeRange);
    if (sortBy && sortBy !== 'newest') next.set('sort_by', sortBy);
    if (userFilter && userFilter !== 'all') next.set('user_filter', userFilter);
    setSearchParams(next);
  };

  const applyFilters = () => commitToUrl(activeTab, 1);

  const resetFilters = () => {
    setTimeRange('all');
    setSortBy('newest');
    setUserFilter('all');
    setFilenameSearch('');
    setSelectedFilters([]);
    commitToUrl(activeTab, 1, { reset: true });
  };

  const changePage = (nextPage: number) => commitToUrl(activeTab, nextPage);

  const toggleCheckbox = (value: string, checked: boolean) => {
    setSelectedFilters((prev) => (checked ? [...prev, value] : prev.filter((v) => v !== value)));
  };

  const renderCheck = (def: CheckDef) => {
    const groupClass =
      def.group === 'tl-sb' ? ' filter-group-tl-sb' : def.group === 'fv' ? ' filter-group-fv' : '';
    return (
      <div className={`rep-pr-check${groupClass}`} key={def.id}>
        <input
          className="form-check-input"
          type="checkbox"
          id={def.id}
          name="filter_type"
          value={def.value}
          checked={selectedFilters.includes(def.value)}
          onChange={(e) => toggleCheckbox(def.value, e.target.checked)}
        />
        <label className="form-check-label" htmlFor={def.id}>
          {def.label}
        </label>
      </div>
    );
  };

  const reuseSettings = async (result: ResultSummary, mode: ResultTab) => {
    const rId = mode === 'fv-results' ? (result.resultId ?? result.processId) : result.processId;
    if (!rId) {
      toast.error('Could not load the filters from this result.');
      return;
    }
    try {
      // Same path as result-detail "Edit criteria" — full filters_applied including
      // Strategic target size / HQ (the hand-rolled draft below used to drop those).
      const fromReuse = await researchService.reuseFilters(rId);
      const legacyResult = result as LegacyResultSummary;
      const fromList =
        (result.filtersApplied as Record<string, unknown> | undefined) ??
        (legacyResult.filters_applied as Record<string, unknown> | undefined) ??
        {};
      const filters = { ...fromList, ...fromReuse };

      if (mode === 'target-list') {
        stashTargetDraft(filtersToTargetForm(filters));
        toast.success('Settings loaded. Opening builder with these criteria.');
        navigate(paths.targetList);
      } else if (mode === 'strategic-buyer') {
        stashStrategicDraft(filtersToStrategicForm(filters));
        toast.success('Settings loaded. Opening builder with these criteria.');
        navigate(paths.strategic);
      } else if (mode === 'fv-results') {
        stashFinancialDraft(filtersToFvForm(filters));
        toast.success('Settings loaded. Opening builder with these criteria.');
        navigate(paths.financialVerticals);
      }
    } catch {
      toast.error('Could not load the filters from this result.');
    }
  };

  const isFvTab = activeTab === 'fv-results';
  const isTsTab = activeTab === 'tearsheets';
  const currentUser = useAuthStore((s) => s.currentUser);
  const currentUserId = currentUser?.id ?? null;

  const visibleRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return results;
    return results.filter((r) => {
      const mandate = resultCrumbName(undefined, r.resultFilename, r.filtersApplied) || '';
      return (
        (r.resultFilename || '').toLowerCase().includes(q) ||
        (r.username || '').toLowerCase().includes(q) ||
        mandate.toLowerCase().includes(q)
      );
    });
  }, [results, searchQuery]);

  const visibleTearsheets = useMemo(() => {
    const companySearch = (
      searchParams.get('search') ||
      searchParams.get('filename_search') ||
      ''
    ).trim();
    return filterAndSortTearsheets(tearsheets, {
      timeRange: searchParams.get('time_range') || 'all',
      sortBy: searchParams.get('sort_by') || 'newest',
      userFilter: searchParams.get('user_filter') || 'all',
      companySearch,
      currentUserId,
    });
  }, [tearsheets, searchParams, currentUserId]);

  const tearsheetPagination = useMemo((): ResultsPagination | null => {
    if (!isTsTab) return null;
    const totalResults = visibleTearsheets.length;
    const totalPages = Math.max(1, Math.ceil(totalResults / TEARSHEETS_PER_PAGE));
    const effectivePage = Math.min(page, totalPages);
    return {
      totalResults,
      page: effectivePage,
      totalPages,
      perPage: TEARSHEETS_PER_PAGE,
      hasPrev: effectivePage > 1,
      hasNext: effectivePage < totalPages,
      prevNum: effectivePage > 1 ? effectivePage - 1 : undefined,
      nextNum: effectivePage < totalPages ? effectivePage + 1 : undefined,
    };
  }, [isTsTab, page, visibleTearsheets]);

  const pagedTearsheets = useMemo(() => {
    if (!tearsheetPagination) return visibleTearsheets;
    const start = (tearsheetPagination.page - 1) * TEARSHEETS_PER_PAGE;
    return visibleTearsheets.slice(start, start + TEARSHEETS_PER_PAGE);
  }, [tearsheetPagination, visibleTearsheets]);

  return (
    <div className={`rep-pr-page${isFvTab ? ' tab-fv-active' : ''}`}>
      <div className="rep-pr-header">
        <div>
          <h1 className="rep-pr-title">Previous Results</h1>
          <p className="rep-pr-subtitle">Lists you've built with Quralyst Research.</p>
        </div>
        <div className="rep-pr-actions">
          {activeTabDef.buildPath && (
            <button
              className="rep-pr-btn-primary btn-unstyled"
              onClick={() => navigate(activeTabDef.buildPath!)}
            >
              <i className="bi bi-plus-lg me-2" /> New{' '}
              {activeTabDef.tabType
                .replace('-results', '')
                .replace('-list', '')
                .replace('-buyer', '')}
            </button>
          )}
        </div>
      </div>

      <div className="rep-pr-toolbar">
        <Tabs
          ariaLabel="Result type"
          className="quralyst-tabs--flush"
          tabs={TABS.map((t) => ({ id: t.tabType, label: t.label }))}
          active={activeTab}
          onChange={(id) => switchTab(id as ResultTab)}
        />
        <div className="rep-pr-search-row">
          <div className="rep-pr-search">
            <i className="bi bi-search search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder={isTsTab ? 'Search by company...' : 'Search by name or user...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeydown}
            />
          </div>
          <button
            type="button"
            className={`rep-pr-btn-filters btn-unstyled${showFilters ? ' active' : ''}`}
            onClick={() => setShowFilters((s) => !s)}
            aria-expanded={showFilters}
          >
            <i className={`bi ${showFilters ? 'bi-funnel-fill' : 'bi-funnel'} me-1`} />
            {showFilters ? 'Hide' : 'Filters'}
            {appliedFilterCount > 0 && (
              <span className="rep-pr-filter-count">{appliedFilterCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* Collapsible filter panel — hidden until Filters is clicked */}
      {showFilters && (
        <div className="rep-pr-filter-panel" id="filterSortSection">
          <div className="rep-pr-filter-grid">
            <div className="rep-pr-filter-field">
              <label
                className="rep-pr-filter-label"
                htmlFor="pr-time-range"
                id="pr-time-range-label"
              >
                <i className="bi bi-calendar-range me-1" />
                Time Range
              </label>
              <Select
                id="pr-time-range"
                aria-labelledby="pr-time-range-label"
                value={timeRange}
                onChange={setTimeRange}
                options={TIME_RANGE_OPTIONS}
                searchable={false}
                buttonClassName="btn-unstyled"
              />
            </div>
            <div className="rep-pr-filter-field">
              <label className="rep-pr-filter-label" htmlFor="pr-sort-by" id="pr-sort-by-label">
                <i className="bi bi-sort-down me-1" />
                Sort By Date
              </label>
              <Select
                id="pr-sort-by"
                aria-labelledby="pr-sort-by-label"
                value={sortBy}
                onChange={setSortBy}
                options={SORT_OPTIONS}
                searchable={false}
                buttonClassName="btn-unstyled"
              />
            </div>
            <div className="rep-pr-filter-field">
              <label
                className="rep-pr-filter-label"
                htmlFor="pr-user-filter"
                id="pr-user-filter-label"
              >
                <i className="bi bi-person me-1" />
                Filter By User
              </label>
              <Select
                id="pr-user-filter"
                aria-labelledby="pr-user-filter-label"
                value={userFilter}
                onChange={setUserFilter}
                options={userOptions}
                searchable={false}
                buttonClassName="btn-unstyled"
              />
            </div>
            {!isFvTab && (
              <div className="rep-pr-filter-field filename-search-group">
                <label className="rep-pr-filter-label" htmlFor="filenameSearch">
                  <i className="bi bi-search me-1" />
                  {isTsTab ? 'Search by Company' : 'Search by Filename'}
                </label>
                <input
                  type="text"
                  className="form-control rep-pr-filter-input"
                  id="filenameSearch"
                  name="filename_search"
                  placeholder={isTsTab ? 'Enter company name...' : 'Enter filename...'}
                  value={filenameSearch}
                  onChange={(e) => setFilenameSearch(e.target.value)}
                />
              </div>
            )}
          </div>

          {!isTsTab && (
            <div
              className="rep-pr-filter-section"
              role="group"
              aria-labelledby="pr-applied-filters-label"
            >
              <div className="rep-pr-filter-label" id="pr-applied-filters-label">
                <i className="bi bi-funnel me-1" />
                Applied Filters (can select multiple)
              </div>
              <div className="rep-pr-checkboxes">{APPLIED_FILTER_CHECKS.map(renderCheck)}</div>
            </div>
          )}

          {!isFvTab && !isTsTab && (
            <div
              className="rep-pr-filter-section"
              role="group"
              aria-labelledby="pr-data-source-label"
            >
              <div className="rep-pr-filter-label" id="pr-data-source-label">
                <i className="bi bi-database me-1" />
                Data Source (can select multiple)
              </div>
              <div className="rep-pr-checkboxes">{DATA_SOURCE_CHECKS.map(renderCheck)}</div>
            </div>
          )}

          <div className="rep-pr-filter-actions">
            <button
              type="button"
              className="rep-pr-btn-primary btn-unstyled"
              onClick={applyFilters}
            >
              <i className="bi bi-check-circle me-1" />
              Apply Filters
            </button>
            <button
              type="button"
              className="rep-pr-btn-outline btn-unstyled"
              onClick={resetFilters}
            >
              <i className="bi bi-arrow-clockwise me-1" />
              Reset
            </button>
          </div>
        </div>
      )}

      <div className="rep-pr-content">
        {loading && (
          <div
            className="rep-pr-list"
            aria-busy="true"
            aria-live="polite"
            aria-label="Loading results"
          >
            {Array.from({ length: 12 }, (_, i) => (
              <div className="rep-pr-row rep-pr-row--skel" key={i} aria-hidden="true">
                <div className="rep-pr-row__head">
                  <span className="rep-pr-skel rep-pr-skel--chevron" />
                  <div className="row-main">
                    <span className="rep-pr-skel rep-pr-skel--icon" />
                    <div className="row-info">
                      <span className="rep-pr-skel rep-pr-skel--title" />
                      <span className="rep-pr-skel rep-pr-skel--meta" />
                    </div>
                    <div className="row-stats">
                      <span className="rep-pr-skel rep-pr-skel--stat" />
                      <span className="rep-pr-skel rep-pr-skel--badge" />
                    </div>
                  </div>
                  <div className="row-action">
                    <span className="rep-pr-skel rep-pr-skel--action" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && loadError && (
          <div className="py-5 text-center">
            <QueryError onRetry={() => commitToUrl(activeTab, page)} />
          </div>
        )}

        {/* ── Tearsheets ── */}
        {!loading && !loadError && isTsTab && (
          <>
            {tearsheets.length === 0 && (
              <div className="rep-pr-empty">
                <div className="empty-icon">
                  <i className="bi bi-inbox" />
                </div>
                <h3>No tearsheets yet</h3>
                <p>Request a tearsheet from any company row in a Target or Strategic list.</p>
              </div>
            )}

            {tearsheets.length > 0 && visibleTearsheets.length === 0 && (
              <div className="rep-pr-empty">
                <div className="empty-icon">
                  <i className="bi bi-search" />
                </div>
                <h3>No matches</h3>
                <p>Try a different company name or adjust your filters.</p>
              </div>
            )}

            {pagedTearsheets.length > 0 && (
              <div className="rep-pr-list">
                {pagedTearsheets.map((ts) => {
                  const effectiveStatus =
                    ts.status === 'complete' && ts.gammaStatus === 'generating'
                      ? 'polishing'
                      : ts.status;
                  const tsPath = buildTearsheetPath(
                    ts.companyName,
                    ts.website,
                    undefined,
                    {
                      country: ts.country,
                      state: ts.state,
                    },
                    ts.id,
                  );
                  const creator =
                    users.find((u) => String(u.id) === String(ts.createdBy))?.fullName ||
                    (currentUser && String(currentUser.id) === String(ts.createdBy)
                      ? [currentUser.profile.firstName, currentUser.profile.lastName]
                          .filter(Boolean)
                          .join(' ') ||
                        currentUser.username ||
                        ''
                      : '');
                  return (
                    <div className="rep-pr-row group" key={ts.id}>
                      <div className="rep-pr-row__head">
                        <button className="row-main btn-unstyled" onClick={() => navigate(tsPath)}>
                          <div className="row-icon">
                            <i className="bi bi-easel" />
                          </div>
                          <div className="row-info">
                            <p className="row-title">{ts.companyName}</p>
                            {ts.oneLiner && <p className="row-desc">{ts.oneLiner}</p>}
                            <p className="row-meta">
                              {formatDate(ts.createdAt)}
                              {creator ? ` · ${creator}` : ''}
                            </p>
                          </div>
                          <div className="row-stats">
                            <span className={`status-badge ${statusVariant(effectiveStatus)}`}>
                              {statusLabel(effectiveStatus)}
                            </span>
                          </div>
                        </button>
                        <div className="row-action">
                          <button
                            className="btn-use-settings btn-unstyled"
                            onClick={() => navigate(tsPath)}
                            title="Open tearsheet"
                          >
                            <i className="bi bi-box-arrow-up-right me-2" /> Open
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {tearsheetPagination && tearsheetPagination.totalPages > 1 && (
              <div className="rep-pr-pagination">
                <p className="pagination-text">
                  Page {tearsheetPagination.page} of {tearsheetPagination.totalPages} ·{' '}
                  {tearsheetPagination.totalResults.toLocaleString()} total
                </p>
                <div className="pagination-controls">
                  <button
                    className="btn-page btn-unstyled"
                    disabled={tearsheetPagination.page <= 1}
                    onClick={() => changePage(Math.max(1, tearsheetPagination.page - 1))}
                  >
                    <i className="bi bi-chevron-left me-1" /> Prev
                  </button>
                  <button
                    className="btn-page btn-unstyled"
                    disabled={tearsheetPagination.page >= tearsheetPagination.totalPages}
                    onClick={() => changePage(tearsheetPagination.page + 1)}
                  >
                    Next <i className="bi bi-chevron-right ms-1" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Lists (Target/Strategic/Financials) ── */}
        {!loading && !loadError && !isTsTab && (
          <>
            {results.length === 0 && (
              <div className="rep-pr-empty">
                <div className="empty-icon">
                  <i className="bi bi-inbox" />
                </div>
                <h3>No {activeTabDef.emptyMode} yet</h3>
                <p>Build your first one to see it here.</p>
                {activeTabDef.buildPath && (
                  <button
                    className="rep-pr-btn-primary mt-3 btn-unstyled"
                    onClick={() => navigate(activeTabDef.buildPath!)}
                  >
                    <i className="bi bi-plus-lg me-2" /> Get started
                  </button>
                )}
              </div>
            )}

            {results.length > 0 && visibleRows.length === 0 && (
              <div className="rep-pr-empty">
                <div className="empty-icon">
                  <i className="bi bi-search" />
                </div>
                <h3>No matches</h3>
                <p>Try a different name or user.</p>
              </div>
            )}

            {visibleRows.length > 0 && (
              <div className="rep-pr-list">
                {visibleRows.map((r) => {
                  const rId = isFvTab ? (r.resultId ?? r.processId) : r.processId;
                  const detailPath = isFvTab
                    ? paths.financialVerticalsResults(rId)
                    : paths.viewResult(rId);
                  const title =
                    resultCrumbName(undefined, r.resultFilename, r.filtersApplied) ||
                    r.resultFilename ||
                    (isFvTab
                      ? 'Untitled result'
                      : activeTab === 'strategic-buyer'
                        ? 'Strategic buyer list'
                        : 'Target list');

                  const open = expandedId === rId;
                  const expandLabel = open
                    ? `Hide applied filters for ${title}`
                    : `Show applied filters for ${title}`;

                  return (
                    <div className={`rep-pr-row group${open ? ' is-open' : ''}`} key={rId}>
                      <div className="rep-pr-row__head">
                        <button
                          type="button"
                          className="rep-pr-expand-btn btn-unstyled"
                          aria-expanded={open}
                          aria-controls={`pr-filters-${rId}`}
                          aria-label={expandLabel}
                          onClick={() => setExpandedId(open ? null : rId)}
                        >
                          <i
                            className={`bi ${open ? 'bi-chevron-down' : 'bi-chevron-right'}`}
                            aria-hidden="true"
                          />
                        </button>
                        <button
                          className="row-main btn-unstyled"
                          onClick={() => navigate(detailPath)}
                        >
                          <div className="row-icon">
                            <i className="bi bi-file-earmark-text" />
                          </div>
                          <div className="row-info">
                            <div className="d-flex align-items-center gap-2">
                              <p className="row-title">{title}</p>
                              {r.version && r.version > 1 && (
                                <span className="version-badge">v{r.version}</span>
                              )}
                              {!isFvTab && (
                                <span
                                  className={`mode-badge mode-badge--${researchModeOf(r.filtersApplied)}`}
                                >
                                  {RESEARCH_MODE_LABEL[researchModeOf(r.filtersApplied)]}
                                </span>
                              )}
                            </div>
                            <p className="row-meta">
                              {formatDate(r.createdAt)} · {r.username}
                            </p>
                          </div>
                          <div className="row-stats">
                            <div className="text-end me-4">
                              <p className="stat-num">
                                {(isFvTab ? r.totalCount : r.totalMatches)?.toLocaleString() || 0}
                              </p>
                              <p className="stat-label">{isFvTab ? 'firms' : 'matches'}</p>
                            </div>
                            {r.status && (
                              <span className={`status-badge ${statusVariant(r.status)}`}>
                                {statusLabel(r.status)}
                              </span>
                            )}
                          </div>
                        </button>
                        <div className="row-action">
                          <button
                            className="btn-use-settings btn-unstyled"
                            onClick={() => void reuseSettings(r, activeTab)}
                            title="Load these settings into the builder"
                          >
                            <i className="bi bi-sliders me-2" /> Use settings{' '}
                            <i className="bi bi-arrow-right ms-2 transition-arrow" />
                          </button>
                        </div>
                      </div>
                      {open ? (
                        <div id={`pr-filters-${rId}`} className="rep-pr-row__details">
                          <PreviousResultExpandPanel
                            result={r}
                            kind={isFvTab ? 'fv' : 'research'}
                          />
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}

            {pagination && pagination.totalPages > 1 && (
              <div className="rep-pr-pagination">
                <p className="pagination-text">
                  Page {pagination.page} of {pagination.totalPages} ·{' '}
                  {pagination.totalResults.toLocaleString()} total
                </p>
                <div className="pagination-controls">
                  <button
                    className="btn-page btn-unstyled"
                    disabled={pagination.page <= 1}
                    onClick={() => changePage(Math.max(1, pagination.page - 1))}
                  >
                    <i className="bi bi-chevron-left me-1" /> Prev
                  </button>
                  <button
                    className="btn-page btn-unstyled"
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => changePage(pagination.page + 1)}
                  >
                    Next <i className="bi bi-chevron-right ms-1" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
